import {
  FC,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { Form, useForm, useFormState } from 'react-final-form';
import { useSelector } from 'react-redux';

import { ProgressButton } from '@beeline/design-system-react/extracted-styles';

import { getSharings } from 'src/api/places';
import { Town } from 'src/api/types';
import { EmployeeInfo } from 'src/components/EmployeeInfo/EmployeeInfo';
import { FormDatesPicker } from 'src/components/FormDatesPicker/FormDatesPicker';
import { Loader } from 'src/components/Loader/Loader';
import { Section } from 'src/components/Section/Section';
import { messagesActions } from 'src/store/messages/slice';
import { getParkingTown } from 'src/store/parking/selectors';
import { shareParkingPlace } from 'src/store/parking/services';
import { useAppDispatch } from 'src/store/useAppDispatch';
import { defaultServerError } from 'src/utils/consts';
import { getDateTime } from 'src/utils/getDateTime';
import { noop } from 'src/utils/noop';
import { areSetsEqual } from 'src/utils/setsEqual';

const getMinDate = (town: Town | undefined): Date => {
  const min = getDateTime({
    timezoneOffset: town?.timeZone,
  });
  min.setDate(min.getDate() - 1);

  return min;
};

const getCurrentDate = (town: Town | undefined, date: Date): string => {
  const currentDate = getDateTime({
    date,
    timezoneOffset: town?.timeZone,
  });
  return `${currentDate.getFullYear()}-${(currentDate.getMonth() + 1).toString().padStart(2, '0')}-${currentDate.getDate()}`;
};

export const ShareActions: FC = () => {
  const { submit } = useForm();
  const { initialValues, values } = useFormState();
  const notChanged = areSetsEqual(initialValues.dates, values.dates);

  const share = useCallback(async () => {
    const result = await submit();
    if (!result) throw new Error('Form is invalid');
  }, [submit]);

  return (
    <ProgressButton
      variant='contained'
      onClick={share}
      fullWidth
      data-testid='sharing'
      disabled={notChanged}
    >
      Сохранить
    </ProgressButton>
  );
};

export interface ShareFieldsProps {
  responsible: string;
}

export const ShareFields: FC<ShareFieldsProps> = ({ responsible }) => {
  const town = useSelector(getParkingTown);

  const min = useMemo(() => {
    return getMinDate(town);
  }, [town]);

  return (
    <>
      <Section title='Закреплено за' testId='responsibleSection'>
        <EmployeeInfo employeeNumber={responsible} testId='responsible' />
      </Section>
      <Section title='Укажи даты шеринга' testId='shareDateSection'>
        <FormDatesPicker name='dates' minDate={min} validateMinMax={noop} />
      </Section>
    </>
  );
};

interface ShareFormValues {
  dates: Set<string>;
}

interface ShareProps {
  placeId: number;
  children?: ReactNode;
}

export const Share: FC<ShareProps> = ({ placeId, children }) => {
  const town = useSelector(getParkingTown);
  const dispatch = useAppDispatch();

  const [initialValues, setInitialValues] = useState<ShareFormValues | null>(
    null,
  );

  const init = useCallback(async () => {
    try {
      const sharings = await getSharings(placeId);
      setInitialValues({
        dates: new Set(
          sharings.map(sharing =>
            getCurrentDate(town, new Date(sharing.sharedFrom)),
          ),
        ),
      });
    } catch (e) {
      dispatch(messagesActions.show({ message: defaultServerError }));
    }
  }, [dispatch, placeId, town]);

  useEffect(() => {
    init();
  }, [init]);

  const share = useCallback(
    async (values: ShareFormValues) => {
      const result = await dispatch(
        shareParkingPlace([...values.dates]),
      ).unwrap();
      if (!result) init();

      return result;
    },
    [dispatch, init],
  );

  return initialValues ? (
    <Form onSubmit={share} initialValues={initialValues}>
      {() => children}
    </Form>
  ) : (
    <Loader />
  );
};
